import { assertNever } from "@redotech/util/type";
import { RedoShopifyDeliveryMethodType } from "../../fulfillments/fulfillment-delivery-method-type";
import { Provider } from "../../order";
import { Team } from "../../team";
import { FlowStep } from "../advanced-flow-db-parser";
import { BaseStep, StepType } from "../common";
import {
  ListToListMatchType,
  StringToListMatchType,
} from "../common/condition";
import { DateComparisonOperator } from "../expressions/date-expression";
import { NumberComparisonOperator } from "../expressions/number-expression";
import { StringMatchOperator } from "../expressions/text-expression";
import { OmsConditionStep } from "../steps";

export enum OmsConditionType {
  ORDER_TAG = "ORDER_TAG",
  SELECTED_SHIPPING = "SELECTED_SHIPPING",
  SKU = "SKU",
  PRODUCT_NAME = "PRODUCT_NAME",
  VARIANT_TITLE = "VARIANT_TITLE",
  PRODUCT_TYPE = "PRODUCT_TYPE",
  ITEM_QUANTITY = "ITEM_QUANTITY",
  PACKAGE_WEIGHT_OZ = "PACKAGE_WEIGHT",
  TOTAL_VALUE = "TOTAL_VALUE",
  TAXES_PAID = "TAXES_PAID",
  DESTINATION_COUNTRY = "DESTINATION_COUNTRY",
  DESTINATION_PROVINCE = "DESTINATION_PROVINCE",
  DELIVERY_METHOD = "DELIVERY_METHOD",
  STREET_ADDRESS = "STREET_ADDRESS",
  STREET_ADDRESS_LIST_INPUT = "STREET_ADDRESS_LIST_INPUT",
  SALES_CHANNEL = "SALES_CHANNEL",
  PRODUCT_TAG = "PRODUCT_TAG",
  FULFILLMENT_LOCATION = "FULFILLMENT_LOCATION",
  ORIGIN_STORE = "ORIGIN_STORE",
  ORDER_NOTES = "ORDER_NOTES",
  FRAUD_RISK = "FRAUD_RISK",
  AVAILABLE_INVENTORY = "AVAILABLE_INVENTORY",
  MARKETPLACE = "MARKETPLACE",
  SHIPMENT_TAG = "SHIPMENT_TAG",
  CUSTOM_STRING_1 = "CUSTOM_STRING_1",
  CUSTOM_STRING_2 = "CUSTOM_STRING_2",
  CUSTOM_STRING_3 = "CUSTOM_STRING_3",
  CUSTOM_NUMBER_1 = "CUSTOM_NUMBER_1",
  CUSTOM_NUMBER_2 = "CUSTOM_NUMBER_2",
  CUSTOM_NUMBER_3 = "CUSTOM_NUMBER_3",
  CUSTOM_DATE_1 = "CUSTOM_DATE_1",
  CUSTOM_DATE_2 = "CUSTOM_DATE_2",
  CUSTOM_DATE_3 = "CUSTOM_DATE_3",
  PRODUCT_CUSTOM_STRING_1 = "PRODUCT_CUSTOM_STRING_1",
  PRODUCT_CUSTOM_STRING_2 = "PRODUCT_CUSTOM_STRING_2",
  PRODUCT_CUSTOM_STRING_3 = "PRODUCT_CUSTOM_STRING_3",
  PRODUCT_CUSTOM_NUMBER_1 = "PRODUCT_CUSTOM_NUMBER_1",
  PRODUCT_CUSTOM_NUMBER_2 = "PRODUCT_CUSTOM_NUMBER_2",
  PRODUCT_CUSTOM_NUMBER_3 = "PRODUCT_CUSTOM_NUMBER_3",
  PRODUCT_CUSTOM_DATE_1 = "PRODUCT_CUSTOM_DATE_1",
  PRODUCT_CUSTOM_DATE_2 = "PRODUCT_CUSTOM_DATE_2",
  PRODUCT_CUSTOM_DATE_3 = "PRODUCT_CUSTOM_DATE_3",
  CUSTOM_ATTRIBUTE_STRING_1 = "CUSTOM_ATTRIBUTE_STRING_1",
  CUSTOM_ATTRIBUTE_STRING_2 = "CUSTOM_ATTRIBUTE_STRING_2",
  CUSTOM_ATTRIBUTE_STRING_3 = "CUSTOM_ATTRIBUTE_STRING_3",
  CUSTOM_ATTRIBUTE_NUMBER_1 = "CUSTOM_ATTRIBUTE_NUMBER_1",
  CUSTOM_ATTRIBUTE_NUMBER_2 = "CUSTOM_ATTRIBUTE_NUMBER_2",
  CUSTOM_ATTRIBUTE_NUMBER_3 = "CUSTOM_ATTRIBUTE_NUMBER_3",
  CUSTOM_ATTRIBUTE_DATE_1 = "CUSTOM_ATTRIBUTE_DATE_1",
  CUSTOM_ATTRIBUTE_DATE_2 = "CUSTOM_ATTRIBUTE_DATE_2",
  CUSTOM_ATTRIBUTE_DATE_3 = "CUSTOM_ATTRIBUTE_DATE_3",
  CUSTOMER_NAME = "CUSTOMER_NAME",
  CUSTOMER_ORDER_COUNT = "CUSTOMER_ORDER_COUNT",
  CUSTOMER_TAG = "CUSTOMER_TAG",
  ITEM_COST = "ITEM_COST",
  VENDOR = "VENDOR",
  USPS_ZONE = "USPS_ZONE",
  LINE_ITEM_CUSTOM_ATTRIBUTE = "LINE_ITEM_CUSTOM_ATTRIBUTE",
  ADDRESS_VERIFIED = "ADDRESS_VERIFIED",
  MARKETPLACE_INTERNAL_NOTES = "MARKETPLACE_INTERNAL_NOTES",
  PACKAGE_IS_LEARNED = "PACKAGE_IS_LEARNED",
}

export const getConditionTypeLabel = (
  conditionType: OmsConditionType,
  team?: Team,
) => {
  switch (conditionType) {
    case OmsConditionType.ORDER_TAG:
      return "Order tags";
    case OmsConditionType.SELECTED_SHIPPING:
      return "Requested shipping service";
    case OmsConditionType.SKU:
      return "Product SKUs";
    case OmsConditionType.PRODUCT_NAME:
      return "Product names";
    case OmsConditionType.VARIANT_TITLE:
      return "Variant titles";
    case OmsConditionType.PRODUCT_TYPE:
      return "Product type";
    case OmsConditionType.ITEM_QUANTITY:
      return "Item quantity";
    case OmsConditionType.PACKAGE_WEIGHT_OZ:
      return "Parcel weight";
    case OmsConditionType.TOTAL_VALUE:
      return "Total value";
    case OmsConditionType.TAXES_PAID:
      return "Taxes paid";
    case OmsConditionType.DESTINATION_COUNTRY:
      return "Destination country";
    case OmsConditionType.DESTINATION_PROVINCE:
      return "Destination state/province";
    case OmsConditionType.DELIVERY_METHOD:
      return "Delivery method";
    case OmsConditionType.STREET_ADDRESS:
      return "Street address";
    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
      return "Street address (list input)";
    case OmsConditionType.SALES_CHANNEL:
      return "Sales channel";
    case OmsConditionType.PRODUCT_TAG:
      return "Product tags";
    case OmsConditionType.FULFILLMENT_LOCATION:
      return "Fulfillment location";
    case OmsConditionType.ORIGIN_STORE:
      return "Origin store";
    case OmsConditionType.ORDER_NOTES:
      return "Order note";
    case OmsConditionType.FRAUD_RISK:
      return "Fraud risk";
    case OmsConditionType.AVAILABLE_INVENTORY:
      return "available inventory when order is placed";
    case OmsConditionType.MARKETPLACE:
      return "Marketplace";
    case OmsConditionType.SHIPMENT_TAG:
      return "Shipment tag";
    case OmsConditionType.CUSTOM_STRING_1:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customString1
          ?.displayName ?? "Custom String 1"
      );
    case OmsConditionType.CUSTOM_STRING_2:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customString2
          ?.displayName ?? "Custom String 2"
      );
    case OmsConditionType.CUSTOM_STRING_3:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customString3
          ?.displayName ?? "Custom String 3"
      );
    case OmsConditionType.CUSTOM_NUMBER_1:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customNumber1
          ?.displayName ?? "Custom Number 1"
      );
    case OmsConditionType.CUSTOM_NUMBER_2:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customNumber2
          ?.displayName ?? "Custom Number 2"
      );
    case OmsConditionType.CUSTOM_NUMBER_3:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customNumber3
          ?.displayName ?? "Custom Number 3"
      );
    case OmsConditionType.CUSTOM_DATE_1:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customDate1
          ?.displayName ?? "Custom Date 1"
      );
    case OmsConditionType.CUSTOM_DATE_2:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customDate2
          ?.displayName ?? "Custom Date 2"
      );
    case OmsConditionType.CUSTOM_DATE_3:
      return (
        team?.settings?.outboundLabels?.customMetadata?.customDate3
          ?.displayName ?? "Custom Date 3"
      );
    case OmsConditionType.PRODUCT_CUSTOM_STRING_1:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomString1?.displayName ?? "Product Custom String 1"
      );
    case OmsConditionType.PRODUCT_CUSTOM_STRING_2:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomString2?.displayName ?? "Product Custom String 2"
      );
    case OmsConditionType.PRODUCT_CUSTOM_STRING_3:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomString3?.displayName ?? "Product Custom String 3"
      );
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_1:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomNumber1?.displayName ?? "Product Custom Number 1"
      );
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_2:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomNumber2?.displayName ?? "Product Custom Number 2"
      );
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_3:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomNumber3?.displayName ?? "Product Custom Number 3"
      );
    case OmsConditionType.PRODUCT_CUSTOM_DATE_1:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomDate1?.displayName ?? "Product Custom Date 1"
      );
    case OmsConditionType.PRODUCT_CUSTOM_DATE_2:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomDate2?.displayName ?? "Product Custom Date 2"
      );
    case OmsConditionType.PRODUCT_CUSTOM_DATE_3:
      return (
        team?.settings?.outboundLabels?.productCustomMetadata
          ?.productCustomDate3?.displayName ?? "Product Custom Date 3"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeString1
          ?.displayName ?? "Custom Attribute 1"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeString2
          ?.displayName ?? "Custom Attribute 2"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeString3
          ?.displayName ?? "Custom Attribute 3"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeNumber1
          ?.displayName ?? "Custom Attribute Number 1"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeNumber2
          ?.displayName ?? "Custom Attribute Number 2"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeNumber3
          ?.displayName ?? "Custom Attribute Number 3"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeDate1
          ?.displayName ?? "Custom Attribute Date 1"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeDate2
          ?.displayName ?? "Custom Attribute Date 2"
      );
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
      return (
        team?.settings?.outboundLabels?.customAttributes?.customAttributeDate3
          ?.displayName ?? "Custom Attribute Date 3"
      );
    case OmsConditionType.CUSTOMER_NAME:
      return "Customer name";
    case OmsConditionType.CUSTOMER_ORDER_COUNT:
      return "Customer order count";
    case OmsConditionType.CUSTOMER_TAG:
      return "Customer tags";
    case OmsConditionType.ITEM_COST:
      return "Item cost";
    case OmsConditionType.VENDOR:
      return "Product vendor";
    case OmsConditionType.USPS_ZONE:
      return "USPS Zone";
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      return "Line item custom attribute";
    case OmsConditionType.ADDRESS_VERIFIED:
      return "Address verified";
    case OmsConditionType.MARKETPLACE_INTERNAL_NOTES:
      return "Marketplace internal notes";
    case OmsConditionType.PACKAGE_IS_LEARNED:
      return "Package is learned";
    default:
      assertNever(conditionType);
  }
};

export const stringMatchOperatorToLabel: Record<StringMatchOperator, string> = {
  startsWith: "starts with",
  endsWith: "ends with",
  includes: "includes",
  equals: "equals",
};

export type BaseWhereStep = Omit<OmsConditionStep, "type" | "id">;

// search-tag:ADDING_AN_OMS_CONDITION
export interface BaseOmsConditionStep extends BaseStep {
  type: StepType.OMS_CONDITION;
  conditionType: OmsConditionType;
  where?: BaseWhereStep[];
  nextTrueId: string;
  nextFalseId: string;
}

export interface OmsOrderTagConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ORDER_TAG;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsSelectedShippingConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.SELECTED_SHIPPING;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsSkuConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.SKU;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsProductNameConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_NAME;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsVariantTitleConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.VARIANT_TITLE;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsProductTypeConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_TYPE;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsItemQuantityConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ITEM_QUANTITY;
  matchType: NumberComparisonOperator;
  matchValue: number;
  where?: OmsProductWhereCondition[];
}

export interface OmsPackageWeightOzConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PACKAGE_WEIGHT_OZ;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsTotalValueConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.TOTAL_VALUE;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsTaxesPaidConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.TAXES_PAID;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsDestinationCountryConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.DESTINATION_COUNTRY;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsDestinationProvinceConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.DESTINATION_PROVINCE;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsDeliveryMethodConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.DELIVERY_METHOD;
  matchType: StringToListMatchType;
  matchValues: RedoShopifyDeliveryMethodType[];
}

export interface OmsStreetAddressConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.STREET_ADDRESS;
  matchType: StringMatchOperator;
  matchValue: string;
}

export interface OmsStreetAddressListInputConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.STREET_ADDRESS_LIST_INPUT;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsSalesChannelConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.SALES_CHANNEL;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsProductTagConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_TAG;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsFulfillmentLocationConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.FULFILLMENT_LOCATION;
  matchType: StringToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsOriginStoreConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ORIGIN_STORE;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsOrderNotesConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ORDER_NOTES;
  matchType: StringMatchOperator;
  matchValue: string;
}

export interface OmsFraudRiskConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.FRAUD_RISK;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsAvailableInventoryConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.AVAILABLE_INVENTORY;
  matchType: NumberComparisonOperator;
  matchValue: number;
  where?: OmsProductWhereCondition[];
}

export interface OmsMarketplaceConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.MARKETPLACE;
  matchType: StringToListMatchType;
  matchValues: Provider[];
}

export interface OmsShipmentTagConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.SHIPMENT_TAG;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsCustomString1ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_STRING_1;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomString2ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_STRING_2;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomString3ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_STRING_3;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomNumber1ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_NUMBER_1;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomNumber2ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_NUMBER_2;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomNumber3ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_NUMBER_3;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomDate1ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_DATE_1;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomDate2ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_DATE_2;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomDate3ConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_DATE_3;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsProductCustomString1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_STRING_1;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsProductCustomString2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_STRING_2;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsProductCustomString3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_STRING_3;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsProductCustomNumber1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_NUMBER_1;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsProductCustomNumber2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_NUMBER_2;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsProductCustomNumber3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_NUMBER_3;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsProductCustomDate1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_DATE_1;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsProductCustomDate2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_DATE_2;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsProductCustomDate3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PRODUCT_CUSTOM_DATE_3;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomAttributeString1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomAttributeString2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomAttributeString3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomAttributeNumber1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomAttributeNumber2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomAttributeNumber3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomAttributeDate1ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomAttributeDate2ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomAttributeDate3ConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3;
  matchType: DateComparisonOperator;
  matchDate: Date;
}

export interface OmsCustomerNameConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOMER_NAME;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsCustomerOrderCountConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOMER_ORDER_COUNT;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsCustomerTagConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.CUSTOMER_TAG;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
}

export interface OmsItemAmountConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ITEM_COST;
  matchType: NumberComparisonOperator;
  matchValue: number;
}

export interface OmsVendorConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.VENDOR;
  matchType: ListToListMatchType;
  matchValues: string[];
  requireExactMatch?: boolean;
  where?: OmsProductWhereCondition[];
}

export interface OmsUspsZoneConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.USPS_ZONE;
  matchType: StringToListMatchType;
  matchValues: string[];
}

export interface OmsLineItemCustomAttributeConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE;
  matchKey: string;
  matchType: StringToListMatchType;
  matchValues: string[];
  where?: OmsProductWhereCondition[];
}

export interface OmsAddressVerifiedConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.ADDRESS_VERIFIED;
  /** true = check if verified, false = check if not verified */
  matchValue: boolean;
}

export interface OmsMarketplaceInternalNotesConditionStep
  extends BaseOmsConditionStep {
  conditionType: OmsConditionType.MARKETPLACE_INTERNAL_NOTES;
  matchType: StringMatchOperator;
  matchValue: string;
}

export interface OmsPackageIsLearnedConditionStep extends BaseOmsConditionStep {
  conditionType: OmsConditionType.PACKAGE_IS_LEARNED;
  matchValue: boolean;
}

export const OmsProductWhereConditionTypes = [
  OmsConditionType.PRODUCT_NAME,
  OmsConditionType.VARIANT_TITLE,
  OmsConditionType.PRODUCT_TYPE,
  OmsConditionType.SKU,
  OmsConditionType.PRODUCT_TAG,
  OmsConditionType.ITEM_QUANTITY,
  OmsConditionType.VENDOR,
];

export type OmsProductWhereCondition =
  | Omit<OmsProductTagConditionStep, "id" | "type">
  | Omit<OmsProductNameConditionStep, "id" | "type">
  | Omit<OmsVariantTitleConditionStep, "id" | "type">
  | Omit<OmsProductTypeConditionStep, "id" | "type">
  | Omit<OmsSkuConditionStep, "id" | "type">
  | Omit<OmsItemQuantityConditionStep, "id" | "type">
  | Omit<OmsVendorConditionStep, "id" | "type">;

export const omsConditionToWhereConditionTypes: Record<
  OmsConditionType,
  OmsConditionType[]
> = {
  [OmsConditionType.ORDER_TAG]: [],
  [OmsConditionType.SELECTED_SHIPPING]: [],
  [OmsConditionType.SKU]: OmsProductWhereConditionTypes,
  [OmsConditionType.PRODUCT_NAME]: OmsProductWhereConditionTypes,
  [OmsConditionType.VARIANT_TITLE]: OmsProductWhereConditionTypes,
  [OmsConditionType.PRODUCT_TYPE]: OmsProductWhereConditionTypes,
  [OmsConditionType.ITEM_QUANTITY]: OmsProductWhereConditionTypes,
  [OmsConditionType.PACKAGE_WEIGHT_OZ]: [],
  [OmsConditionType.TOTAL_VALUE]: [],
  [OmsConditionType.TAXES_PAID]: [],
  [OmsConditionType.DESTINATION_COUNTRY]: [],
  [OmsConditionType.DESTINATION_PROVINCE]: [],
  [OmsConditionType.DELIVERY_METHOD]: [],
  [OmsConditionType.STREET_ADDRESS]: [],
  [OmsConditionType.STREET_ADDRESS_LIST_INPUT]: [],
  [OmsConditionType.SALES_CHANNEL]: [],
  [OmsConditionType.PRODUCT_TAG]: OmsProductWhereConditionTypes,
  [OmsConditionType.FULFILLMENT_LOCATION]: [],
  [OmsConditionType.ORIGIN_STORE]: [],
  [OmsConditionType.ORDER_NOTES]: [],
  [OmsConditionType.FRAUD_RISK]: [],
  [OmsConditionType.AVAILABLE_INVENTORY]: [],
  [OmsConditionType.MARKETPLACE]: [],
  [OmsConditionType.SHIPMENT_TAG]: [],
  [OmsConditionType.CUSTOM_STRING_1]: [],
  [OmsConditionType.CUSTOM_STRING_2]: [],
  [OmsConditionType.CUSTOM_STRING_3]: [],
  [OmsConditionType.CUSTOM_NUMBER_1]: [],
  [OmsConditionType.CUSTOM_NUMBER_2]: [],
  [OmsConditionType.CUSTOM_NUMBER_3]: [],
  [OmsConditionType.CUSTOM_DATE_1]: [],
  [OmsConditionType.CUSTOM_DATE_2]: [],
  [OmsConditionType.CUSTOM_DATE_3]: [],
  [OmsConditionType.PRODUCT_CUSTOM_STRING_1]: [],
  [OmsConditionType.PRODUCT_CUSTOM_STRING_2]: [],
  [OmsConditionType.PRODUCT_CUSTOM_STRING_3]: [],
  [OmsConditionType.PRODUCT_CUSTOM_NUMBER_1]: [],
  [OmsConditionType.PRODUCT_CUSTOM_NUMBER_2]: [],
  [OmsConditionType.PRODUCT_CUSTOM_NUMBER_3]: [],
  [OmsConditionType.PRODUCT_CUSTOM_DATE_1]: [],
  [OmsConditionType.PRODUCT_CUSTOM_DATE_2]: [],
  [OmsConditionType.PRODUCT_CUSTOM_DATE_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2]: [],
  [OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3]: [],
  [OmsConditionType.CUSTOMER_NAME]: [],
  [OmsConditionType.CUSTOMER_ORDER_COUNT]: [],
  [OmsConditionType.CUSTOMER_TAG]: [],
  [OmsConditionType.ITEM_COST]: [],
  [OmsConditionType.VENDOR]: OmsProductWhereConditionTypes,
  [OmsConditionType.USPS_ZONE]: [],
  [OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE]: OmsProductWhereConditionTypes,
  [OmsConditionType.ADDRESS_VERIFIED]: [],
  [OmsConditionType.MARKETPLACE_INTERNAL_NOTES]: [],
  [OmsConditionType.PACKAGE_IS_LEARNED]: [],
};

/**
 * Compares two OMS condition steps for equality based on their specific fields.
 * Returns true if both conditions have the same type and matching condition-specific parameters.
 */
export function areConditionsEqual(
  condition1: FlowStep | null | undefined,
  condition2: FlowStep | null | undefined,
): boolean {
  if (!condition1 || !condition2) {
    return condition1 === condition2;
  }

  if (
    condition1.type !== StepType.OMS_CONDITION ||
    condition2.type !== StepType.OMS_CONDITION
  ) {
    return false;
  }

  if (condition1.conditionType !== condition2.conditionType) {
    return false;
  }

  const arraysEqual = (arr1?: any[], arr2?: any[]) => {
    if (!arr1 && !arr2) {
      return true;
    }
    if (!arr1 || !arr2) {
      return false;
    }
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((val, index) => val === arr2[index]);
  };

  const datesEqual = (date1?: Date, date2?: Date) => {
    if (!date1 && !date2) {
      return true;
    }
    if (!date1 || !date2) {
      return false;
    }
    return date1.getTime() === date2.getTime();
  };

  switch (condition1.conditionType) {
    case OmsConditionType.ORDER_TAG:
      return (
        condition1.matchType ===
          (condition2 as OmsOrderTagConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsOrderTagConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsOrderTagConditionStep).requireExactMatch
      );

    case OmsConditionType.SELECTED_SHIPPING:
      return (
        condition1.matchType ===
          (condition2 as OmsSelectedShippingConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsSelectedShippingConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsSelectedShippingConditionStep).requireExactMatch
      );

    case OmsConditionType.SKU:
      return (
        condition1.matchType ===
          (condition2 as OmsSkuConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsSkuConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsSkuConditionStep).requireExactMatch
      );

    case OmsConditionType.PRODUCT_NAME:
      return (
        condition1.matchType ===
          (condition2 as OmsProductNameConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductNameConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsProductNameConditionStep).requireExactMatch
      );

    case OmsConditionType.VARIANT_TITLE:
      return (
        condition1.matchType ===
          (condition2 as OmsVariantTitleConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsVariantTitleConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsVariantTitleConditionStep).requireExactMatch
      );

    case OmsConditionType.PRODUCT_TYPE:
      return (
        condition1.matchType ===
          (condition2 as OmsProductTypeConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductTypeConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsProductTypeConditionStep).requireExactMatch
      );

    case OmsConditionType.ITEM_QUANTITY:
      return (
        condition1.matchType ===
          (condition2 as OmsItemQuantityConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsItemQuantityConditionStep).matchValue
      );

    case OmsConditionType.PACKAGE_WEIGHT_OZ:
      return (
        condition1.matchType ===
          (condition2 as OmsPackageWeightOzConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsPackageWeightOzConditionStep).matchValue
      );

    case OmsConditionType.TOTAL_VALUE:
      return (
        condition1.matchType ===
          (condition2 as OmsTotalValueConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsTotalValueConditionStep).matchValue
      );

    case OmsConditionType.TAXES_PAID:
      return (
        condition1.matchType ===
          (condition2 as OmsTaxesPaidConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsTaxesPaidConditionStep).matchValue
      );

    case OmsConditionType.DESTINATION_COUNTRY:
      return (
        condition1.matchType ===
          (condition2 as OmsDestinationCountryConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsDestinationCountryConditionStep).matchValues,
        )
      );

    case OmsConditionType.DESTINATION_PROVINCE:
      return (
        condition1.matchType ===
          (condition2 as OmsDestinationProvinceConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsDestinationProvinceConditionStep).matchValues,
        )
      );

    case OmsConditionType.DELIVERY_METHOD:
      return (
        condition1.matchType ===
          (condition2 as OmsDeliveryMethodConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsDeliveryMethodConditionStep).matchValues,
        )
      );

    case OmsConditionType.STREET_ADDRESS:
      return (
        condition1.matchType ===
          (condition2 as OmsStreetAddressConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsStreetAddressConditionStep).matchValue
      );

    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
      return (
        condition1.matchType ===
          (condition2 as OmsStreetAddressListInputConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsStreetAddressListInputConditionStep).matchValues,
        )
      );

    case OmsConditionType.SALES_CHANNEL:
      return (
        condition1.matchType ===
          (condition2 as OmsSalesChannelConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsSalesChannelConditionStep).matchValues,
        )
      );

    case OmsConditionType.PRODUCT_TAG:
      return (
        condition1.matchType ===
          (condition2 as OmsProductTagConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductTagConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsProductTagConditionStep).requireExactMatch
      );

    case OmsConditionType.FULFILLMENT_LOCATION:
      return (
        condition1.matchType ===
          (condition2 as OmsFulfillmentLocationConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsFulfillmentLocationConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsFulfillmentLocationConditionStep).requireExactMatch
      );

    case OmsConditionType.ORIGIN_STORE:
      return (
        condition1.matchType ===
          (condition2 as OmsOriginStoreConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsOriginStoreConditionStep).matchValues,
        )
      );

    case OmsConditionType.ORDER_NOTES:
      return (
        condition1.matchType ===
          (condition2 as OmsOrderNotesConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsOrderNotesConditionStep).matchValue
      );

    case OmsConditionType.FRAUD_RISK:
      return (
        condition1.matchType ===
          (condition2 as OmsFraudRiskConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsFraudRiskConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsFraudRiskConditionStep).requireExactMatch
      );

    case OmsConditionType.AVAILABLE_INVENTORY:
      return (
        condition1.matchType ===
          (condition2 as OmsAvailableInventoryConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsAvailableInventoryConditionStep).matchValue
      );

    case OmsConditionType.MARKETPLACE:
      return (
        condition1.matchType ===
          (condition2 as OmsMarketplaceConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsMarketplaceConditionStep).matchValues,
        )
      );

    case OmsConditionType.SHIPMENT_TAG:
      return (
        condition1.matchType ===
          (condition2 as OmsShipmentTagConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsShipmentTagConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsShipmentTagConditionStep).requireExactMatch
      );

    case OmsConditionType.CUSTOM_STRING_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomString1ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomString1ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_STRING_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomString2ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomString2ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_STRING_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomString3ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomString3ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_NUMBER_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomNumber1ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomNumber1ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_NUMBER_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomNumber2ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomNumber2ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_NUMBER_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomNumber3ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomNumber3ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_DATE_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomDate1ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomDate1ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOM_DATE_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomDate2ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomDate2ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOM_DATE_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomDate3ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomDate3ConditionStep).matchDate,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_STRING_1:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomString1ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductCustomString1ConditionStep).matchValues,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_STRING_2:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomString2ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductCustomString2ConditionStep).matchValues,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_STRING_3:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomString3ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsProductCustomString3ConditionStep).matchValues,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_1:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomNumber1ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsProductCustomNumber1ConditionStep).matchValue
      );

    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_2:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomNumber2ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsProductCustomNumber2ConditionStep).matchValue
      );

    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_3:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomNumber3ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsProductCustomNumber3ConditionStep).matchValue
      );

    case OmsConditionType.PRODUCT_CUSTOM_DATE_1:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomDate1ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsProductCustomDate1ConditionStep).matchDate,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_DATE_2:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomDate2ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsProductCustomDate2ConditionStep).matchDate,
        )
      );

    case OmsConditionType.PRODUCT_CUSTOM_DATE_3:
      return (
        condition1.matchType ===
          (condition2 as OmsProductCustomDate3ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsProductCustomDate3ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeString1ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomAttributeString1ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeString2ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomAttributeString2ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeString3ConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomAttributeString3ConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeNumber1ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomAttributeNumber1ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeNumber2ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomAttributeNumber2ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeNumber3ConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomAttributeNumber3ConditionStep).matchValue
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeDate1ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomAttributeDate1ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeDate2ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomAttributeDate2ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomAttributeDate3ConditionStep).matchType &&
        datesEqual(
          condition1.matchDate,
          (condition2 as OmsCustomAttributeDate3ConditionStep).matchDate,
        )
      );

    case OmsConditionType.CUSTOMER_NAME:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomerNameConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomerNameConditionStep).matchValues,
        )
      );

    case OmsConditionType.CUSTOMER_ORDER_COUNT:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomerOrderCountConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsCustomerOrderCountConditionStep).matchValue
      );

    case OmsConditionType.CUSTOMER_TAG:
      return (
        condition1.matchType ===
          (condition2 as OmsCustomerTagConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsCustomerTagConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsCustomerTagConditionStep).requireExactMatch
      );

    case OmsConditionType.ITEM_COST:
      return (
        condition1.matchType ===
          (condition2 as OmsItemAmountConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsItemAmountConditionStep).matchValue
      );

    case OmsConditionType.VENDOR:
      return (
        condition1.matchType ===
          (condition2 as OmsVendorConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsVendorConditionStep).matchValues,
        ) &&
        condition1.requireExactMatch ===
          (condition2 as OmsVendorConditionStep).requireExactMatch
      );

    case OmsConditionType.USPS_ZONE:
      return (
        condition1.matchType ===
          (condition2 as OmsUspsZoneConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsUspsZoneConditionStep).matchValues,
        )
      );

    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      return (
        condition1.matchKey ===
          (condition2 as OmsLineItemCustomAttributeConditionStep).matchKey &&
        condition1.matchType ===
          (condition2 as OmsLineItemCustomAttributeConditionStep).matchType &&
        arraysEqual(
          condition1.matchValues,
          (condition2 as OmsLineItemCustomAttributeConditionStep).matchValues,
        )
      );

    case OmsConditionType.ADDRESS_VERIFIED:
      return (
        condition1.matchValue ===
        (condition2 as OmsAddressVerifiedConditionStep).matchValue
      );

    case OmsConditionType.MARKETPLACE_INTERNAL_NOTES:
      return (
        condition1.matchType ===
          (condition2 as OmsMarketplaceInternalNotesConditionStep).matchType &&
        condition1.matchValue ===
          (condition2 as OmsMarketplaceInternalNotesConditionStep).matchValue
      );

    case OmsConditionType.PACKAGE_IS_LEARNED:
      return (
        condition1.matchValue ===
        (condition2 as OmsPackageIsLearnedConditionStep).matchValue
      );

    default:
      assertNever(condition1);
  }
}
